import { Link } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight } from "lucide-react";

import { DocsBlockView } from "@/presentation/components/docs/docs-block-view";
import type { DocsPage } from "@/presentation/data/docs/docs-content.types";
import { flattenDocsPages } from "@/presentation/data/docs/docs-manifest";

interface DocsPageContentProps {
  page: DocsPage;
}

export function DocsPageContent({ page }: DocsPageContentProps) {
  return (
    <article className="min-w-0">
      <header className="mb-6">
        <h1 className="text-foreground text-2xl font-semibold tracking-tight">{page.title}</h1>
        <p className="text-muted-foreground mt-1 text-sm">{page.description}</p>
        {page.stub ? (
          <span className="bg-muted text-muted-foreground mt-3 inline-block rounded-full px-2 py-0.5 font-mono text-[10px] tracking-wide uppercase">
            Coming soon
          </span>
        ) : null}
      </header>
      {page.blocks.map((block, i) => (
        <DocsBlockView key={i} block={block} />
      ))}
      <DocsPageNav slug={page.slug} />
    </article>
  );
}

function DocsPageNav({ slug }: { slug: string }) {
  const flat = flattenDocsPages();
  const index = flat.findIndex((loc) => loc.page.slug === slug);
  const prev = index > 0 ? flat[index - 1] : null;
  const next = index >= 0 && index < flat.length - 1 ? flat[index + 1] : null;

  if (!prev && !next) return null;

  return (
    <nav className="mt-12 flex items-center justify-between gap-4 border-t border-[var(--border)] pt-6">
      {prev ? (
        <Link
          to="/docs"
          search={{ page: prev.page.slug }}
          className="text-muted-foreground hover:text-foreground hover:border-primary/40 group flex flex-col gap-0.5 rounded-lg border border-[var(--border)] px-4 py-2.5 transition-colors"
        >
          <span className="flex items-center gap-1 font-mono text-[10px] tracking-wide uppercase">
            <ArrowLeft className="size-3" /> Previous
          </span>
          <span className="text-foreground text-sm font-medium">{prev.page.title}</span>
        </Link>
      ) : (
        <span />
      )}
      {next ? (
        <Link
          to="/docs"
          search={{ page: next.page.slug }}
          className="text-muted-foreground hover:text-foreground hover:border-primary/40 group flex flex-col items-end gap-0.5 rounded-lg border border-[var(--border)] px-4 py-2.5 text-right transition-colors"
        >
          <span className="flex items-center gap-1 font-mono text-[10px] tracking-wide uppercase">
            Next <ArrowRight className="size-3" />
          </span>
          <span className="text-foreground text-sm font-medium">{next.page.title}</span>
        </Link>
      ) : (
        <span />
      )}
    </nav>
  );
}
